import React from "react";
import {
  ActivityIndicator,
  FlatList,
  SafeAreaView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { Room } from "../../../../types/types";
import MeetingOption from "../components/MeetingOption";
import Illustration from "../../../../assets/Illustration";
import { FeedbackFabButton } from "../../../../components/FeedbackFabButton";
import CreateMeetingButton from "../components/CreateMeetingButton";

type Props = {
  rooms: Room[];
  loading: boolean;
  errorText: string | null;

  getParticipantsCount: (room: Room) => number;
  isRoomDisabled: (room: Room) => boolean;
  getDisabledActionLabel: (room: Room) => string | undefined;

  onRoomPress: (room: Room) => void;
  onPressSchedule: () => void;
};

export default function AllMeetingsView({
  rooms,
  loading,
  errorText,
  getParticipantsCount,
  isRoomDisabled,
  getDisabledActionLabel,
  onRoomPress,
  onPressSchedule,
}: Props) {
  const { width } = useWindowDimensions();
  const minCardWidth = 360;
  const columns = Math.max(
    1,
    Math.floor((width - 80 + 24) / (minCardWidth + 24)),
  );

  return (
    <SafeAreaView style={styles.container}>
      {loading && <ActivityIndicator size="large" style={{ marginTop: 20 }} />}
      {errorText && <Text style={styles.errorText}>{errorText}</Text>}

      <FlatList
        key={`columns-${columns}`}
        numColumns={columns}
        data={rooms}
        keyExtractor={(item: Room) => item.id}
        renderItem={({ item }: { item: Room }) => (
          <MeetingOption
            room={item}
            isSelected={false}
            onSelect={() => onRoomPress(item)}
            participantsCount={getParticipantsCount(item)}
            isDisabled={isRoomDisabled(item)}
            disabledActionLabel={getDisabledActionLabel(item)}
          />
        )}
        contentContainerStyle={styles.listContainer}
        columnWrapperStyle={columns > 1 ? styles.columnWrapper : undefined}
        ListEmptyComponent={
          !loading ? (
            <View style={styles.emptyContainer}>
              <Illustration width={415} height={362} />
              <Text style={styles.emptyTitle}>Available Conversations</Text>
              <Text style={styles.emptyText}>
                There are no conversations available right now.{"\n"}
                You can check back later, or start your own when you feel ready.
              </Text>
              <CreateMeetingButton onPress={onPressSchedule} />
            </View>
          ) : null
        }
      />

      <FeedbackFabButton />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F6F7F9" },
  listContainer: { paddingHorizontal: 40, paddingBottom: 100, flexGrow: 1 },
  columnWrapper: {
    justifyContent: "flex-start",
    gap: 24,
  },
  errorText: { color: "red", textAlign: "center" },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 44,
    fontWeight: "700",
    fontFamily: "Lexend",
    color: "#2B2B2F",
    textAlign: "center",
    marginTop: 40,
    marginBottom: 16,
    lineHeight: 50,
  },
  emptyText: {
    fontSize: 22,
    fontWeight: "400",
    fontFamily: "Lexend",
    color: "#2B2B2F",
    textAlign: "center",
    opacity: 0.6,
    lineHeight: 30,
    maxWidth: 800,
  },
});
